import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Search } from "lucide-react";

const CATS = ["all", "housing", "food", "education", "medical", "seniors", "environment", "children", "emergency"];
const URGENCY = ["all", "low", "normal", "high", "emergency"];

export default function FindHelp() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState(params.get("category") || "all");
  const [urgency, setUrgency] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category !== "all") p.category = category;
    if (urgency !== "all") p.urgency = urgency;
    http.get("/help-requests", { params: p }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [category, urgency]);

  const filtered = items.filter((i) => !q || i.title.toLowerCase().includes(q.toLowerCase()) || i.description.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="find-help-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <div className="overline mb-3">FIND HELP</div>
      <h1 className="font-display text-4xl md:text-5xl max-w-2xl">People near you and around the world need a hand.</h1>

      <div className="mt-10 flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="flex items-center gap-2 flex-1 rounded-full border px-4 py-3" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}>
          <Search className="w-4 h-4 opacity-60" />
          <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search requests…" className="flex-1 bg-transparent outline-none text-sm" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select data-testid="category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-full border px-4 py-3 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }}>
            {CATS.map((c) => <option key={c} value={c}>Category: {c}</option>)}
          </select>
          <select data-testid="urgency-filter" value={urgency} onChange={(e) => setUrgency(e.target.value)} className="rounded-full border px-4 py-3 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }}>
            {URGENCY.map((u) => <option key={u} value={u}>Urgency: {u}</option>)}
          </select>
        </div>
        <Link to="/request-help" data-testid="request-help-link" className="wofy-btn-primary whitespace-nowrap">Request help</Link>
      </div>

      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading && ["s1", "s2", "s3", "s4", "s5", "s6"].map((k) => (
          <div key={k} className="wofy-card animate-pulse h-48" />
        ))}
        {!loading && filtered.length === 0 && (
          <div className="col-span-full text-center py-16" style={{ color: "var(--wofy-text-soft)" }}>
            No requests match right now. Be the first to <Link to="/request-help" className="underline">post a request</Link>.
          </div>
        )}
        {filtered.map((r) => (
          <div key={r.id} data-testid={`request-card-${r.id}`} className="wofy-card">
            <div className="flex items-center justify-between">
              <span className="wofy-tag">{r.category}</span>
              <span className="wofy-tag" style={{ color: r.urgency === "emergency" ? "#B25D38" : undefined }}>{r.urgency}</span>
            </div>
            <div className="font-display text-xl mt-4 leading-snug">{r.title}</div>
            <p className="mt-2 text-sm line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>{r.description}</p>
            <div className="mt-5 flex items-center justify-between text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>
              <span>{r.city}, {r.country}</span>
              <span>{r.supporters} supporters</span>
            </div>
            <div className="mt-4 text-xs" style={{ color: "var(--wofy-text-soft)" }}>Posted by {r.user_name}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
